import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import BookingModal from "@/components/BookingModal";
import { services, filterCategories } from "@/data/services";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, delay, ease: "easeOut" },
});

function ServiceCard({ service, onBook, index }) {
  const isPremium = service.badge === "PREMIUM";
  return (
    <motion.div
      className={`relative bg-[#0D0D1A] border rounded-2xl p-10 group overflow-hidden transition-all duration-500 ${
        isPremium
          ? "border-[#C9A84C]/25 hover:border-[#C9A84C]/60"
          : "border-[#1A1A2E] hover:border-[#9B72CF]/40"
      }`}
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.07 }}
      whileHover={{ scale: 1.01 }}
      data-testid={`service-card-${service.id}`}
    >
      {/* Glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none"
        style={{
          background: isPremium
            ? "radial-gradient(circle at 50% 0%, rgba(201,168,76,0.06), transparent 70%)"
            : "radial-gradient(circle at 50% 0%, rgba(155,114,207,0.06), transparent 70%)",
        }}
      />

      {service.badge && (
        <span
          className={`absolute top-5 right-5 text-[0.58rem] uppercase tracking-[0.18em] px-3 py-1 ${
            isPremium
              ? "text-[#050510] bg-[#C9A84C]"
              : "text-[#050510] bg-[#C9A84C]"
          }`}
        >
          {service.badge}
        </span>
      )}

      <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#C9A84C] mb-3">
        {service.categoryLabel} · {service.mode}
      </p>
      <h2
        className="text-2xl md:text-3xl font-light text-[#F0EDE8] mb-4 leading-snug"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
      >
        {service.name}
      </h2>
      <p className="text-[#9A9A9A] text-sm leading-relaxed mb-6">
        {service.fullDescription}
      </p>

      {/* Highlights */}
      <div className="flex flex-wrap gap-2 mb-8">
        {service.shortDescription.split(" · ").map((item) => (
          <span
            key={item}
            className="text-xs text-[#9A9A9A] border border-[#1A1A2E] px-3 py-1 rounded-full"
          >
            {item}
          </span>
        ))}
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          {service.pricingTiers ? (
            <>
              <p className="text-[0.6rem] uppercase tracking-[0.15em] text-[#9A9A9A] mb-1">
                ab
              </p>
              <span
                className="text-3xl text-[#C9A84C] font-light"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {service.startingFrom}
              </span>
              <p className="text-[#9A9A9A] text-xs mt-1">
                3 Format-Optionen verfügbar
              </p>
            </>
          ) : (
            <span
              className="text-2xl text-[#C9A84C] font-light"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {service.startingFrom}
            </span>
          )}
        </div>

        {service.bookingType === "modal" ? (
          <button
            onClick={() => onBook(service)}
            className="btn-gold-shimmer flex items-center gap-2 border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[0.18em] px-8 py-3 hover:bg-[#C9A84C] hover:text-[#050510] transition-all duration-300"
            data-testid={`book-btn-${service.id}`}
          >
            Jetzt buchen <ArrowRight size={13} />
          </button>
        ) : (
          <Link
            to="/kontakt"
            className="flex items-center gap-2 border border-[#1A1A2E] text-[#9A9A9A] text-xs uppercase tracking-[0.18em] px-8 py-3 hover:border-[#C9A84C] hover:text-[#C9A84C] transition-all duration-300"
            data-testid={`contact-btn-${service.id}`}
          >
            Termin anfragen <ArrowRight size={13} />
          </Link>
        )}
      </div>
    </motion.div>
  );
}

export default function Leistungen() {
  const [activeCategory, setActiveCategory] = useState("alle");
  const [bookingService, setBookingService] = useState(null);

  const filtered =
    activeCategory === "alle"
      ? services
      : services.filter((s) => s.category === activeCategory);

  const energieServices = filtered.filter((s) => s.category === "energie");
  const aufstellungServices = filtered.filter((s) => s.category === "aufstellung");
  const tiereServices = filtered.filter((s) => s.category === "tiere");
  const coachingServices = filtered.filter((s) => s.category === "coaching");

  return (
    <div className="bg-[#050510] text-[#F0EDE8] min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div className="text-center mb-16" {...fadeUp()}>
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-[#C9A84C] mb-4">
            Leistungen
          </p>
          <h1
            className="text-5xl md:text-6xl font-light text-[#F0EDE8] mb-6"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Meine Angebote
          </h1>
          <p className="text-[#9A9A9A] text-lg max-w-2xl mx-auto leading-relaxed">
            Alle Sessions finden remote statt. Je nach Wunsch als schriftliche
            Auswertung, WhatsApp-Sprachnachricht oder 1:1 Live-Sitzung.
          </p>
        </motion.div>

        {/* Filter */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-2 mb-14"
          {...fadeUp(0.1)}
        >
          {filterCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`text-xs uppercase tracking-[0.2em] px-6 py-2.5 border transition-all duration-300 ${
                activeCategory === cat.id
                  ? "border-[#C9A84C] bg-[#C9A84C] text-[#050510]"
                  : "border-[#1A1A2E] text-[#9A9A9A] hover:border-[#C9A84C]/50 hover:text-[#F0EDE8]"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* Energetische Sessions */}
        {energieServices.length > 0 && (
          <section className="mb-16">
            <motion.h2
              className="text-xs uppercase tracking-[0.4em] text-[#C9A84C] mb-8 flex items-center gap-4"
              {...fadeUp()}
            >
              Energetische Sessions
              <span className="flex-1 h-px bg-[#1A1A2E]" />
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {energieServices.map((s, i) => (
                <ServiceCard key={s.id} service={s} onBook={setBookingService} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Tiere */}
        {tiereServices.length > 0 && (
          <section className="mb-16">
            <motion.h2
              className="text-xs uppercase tracking-[0.4em] text-[#C9A84C] mb-8 flex items-center gap-4"
              {...fadeUp()}
            >
              Tierkommunikation
              <span className="flex-1 h-px bg-[#1A1A2E]" />
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {tiereServices.map((s, i) => (
                <ServiceCard key={s.id} service={s} onBook={setBookingService} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Premium Aufstellungen */}
        {aufstellungServices.length > 0 && (
          <section className="mb-16">
            <motion.h2
              className="text-xs uppercase tracking-[0.4em] text-[#C9A84C] mb-2 flex items-center gap-4"
              {...fadeUp()}
            >
              Premium Aufstellungen
              <span className="flex-1 h-px bg-[#1A1A2E]" />
            </motion.h2>
            <motion.p
              className="text-[#9A9A9A] text-sm mb-8 max-w-3xl"
              {...fadeUp(0.1)}
            >
              Energetische Aufstellungen machen verborgene Zusammenhänge, unbewusste
              Muster und systemische Verstrickungen sichtbar — für Familie, Partnerschaft,
              Beruf oder persönliche Entwicklung.
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {aufstellungServices.map((s, i) => (
                <ServiceCard key={s.id} service={s} onBook={setBookingService} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Coaching */}
        {coachingServices.length > 0 && (
          <section className="mb-16">
            <motion.h2
              className="text-xs uppercase tracking-[0.4em] text-[#C9A84C] mb-8 flex items-center gap-4"
              {...fadeUp()}
            >
              Coaching & Begleitung
              <span className="flex-1 h-px bg-[#1A1A2E]" />
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {coachingServices.map((s, i) => (
                <ServiceCard key={s.id} service={s} onBook={setBookingService} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Disclaimer */}
        <motion.div
          className="mt-8 p-6 border border-[#1A1A2E] rounded-xl"
          {...fadeUp()}
        >
          <p className="text-[#9A9A9A] text-xs leading-relaxed text-center">
            Die energetische Arbeit kann je nach Situation durch den Einsatz eines
            Pendels oder Karten ergänzt werden. Alle Leistungen ersetzen keine
            medizinische, psychologische oder tierärztliche Behandlung.
          </p>
        </motion.div>
      </div>

      {bookingService && (
        <BookingModal
          service={bookingService}
          onClose={() => setBookingService(null)}
        />
      )}
    </div>
  );
}
